import React, { ReactNode } from 'react';

import {
  Box,
  CircularProgress,
  Typography,
  TypographyProps,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { useStyles } from './ViewField.styles';

export type ViewFieldLabelVariant = 'standard' | 'primary';

export interface IViewFieldBaseProps {
  label: ReactNode;
  tooltip?: ReactNode | null;
  value: string | number | ReactNode;
  /** Show loader instead of value */
  loading?: boolean;
  /** Label color: standard (secondary) or primary */
  labelVariant?: ViewFieldLabelVariant;
  /** Typography variant for the value (e.g. 'text-lg', 'h1', 'h2') */
  valueVariant?: TypographyProps['variant'];
  /** Color for the value (e.g. 'inherit', 'primary', 'secondary') */
  valueColor?: TypographyProps['color'];
  /** Disable container box shadow */
  noShadow?: boolean;
  /** Icon or other component on the left */
  startAdornment?: ReactNode;
  className?: string;
}

export function ViewFieldBase({
  label,
  tooltip = null,
  value,
  loading = false,
  labelVariant = 'standard',
  valueVariant = 'text-lg',
  valueColor = 'inherit',
  noShadow = false,
  startAdornment,
  className,
}: IViewFieldBaseProps) {
  const theme = useTheme();

  const { classes } = useStyles(theme);

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      className={clsx(
        classes.viewFieldWrapper,
        noShadow && classes.viewFieldWrapperNoShadow,
        className,
      )}>
      {startAdornment != null && (
        <Box className={classes.startAdornment}>{startAdornment}</Box>
      )}
      <Box
        className={classes.viewFieldContent}
        display="flex"
        flexDirection="column"
        width="100%">
        <Box className={classes.labelRow}>
          <Typography
            variant="label-text-md"
            className={clsx(
              classes.label,
              labelVariant === 'primary' && classes.labelPrimary,
            )}>
            {label}
          </Typography>
          {tooltip !== null && <Box className={classes.tooltip}>{tooltip}</Box>}
        </Box>
        {loading ? (
          <Box display="flex" alignItems="center" width="100%">
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Typography
            className={classes.typography}
            variant={valueVariant}
            color={valueColor}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
